import { Box, debounce, Typography } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useRef, useState } from 'react';
import { EChartsOption, SeriesOption } from 'echarts';
import dayjs from 'dayjs';

interface ChartDataPoint {
	[key: string]: string | number | undefined;
	date: string;
	OverallGrowth?: number;
}

interface StackedBarLineTrendChartProps {
	title: any;
	isLoading?: boolean;
	data: ChartDataPoint[];
  filter?: any[];
	colorMapping?: Record<string, string>;
	optionsFunction?: (data: ChartDataPoint[], colorMap: Record<string, string>) => EChartsOption;
}

export const getStackedBarLineChartOptions = (
  chartData: ChartDataPoint[] = [],
  colorMapping: Record<string, string> = {},
  filter?: any[]
): EChartsOption => {

  if (!chartData.length) return {};

  // extract all category keys from the chartData
  const allCategories = chartData.reduce((categories: Set<string>, item: ChartDataPoint) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'month' && key !== 'overallGrowthPercentage' && key !== 'totalSizeInBytes' && key !== 'unit') {
        categories.add(key);
      }
    });
    return categories;
  }, new Set<string>());

  const filteredCategories = Array.from(allCategories).filter((category) =>
    chartData.some((item) => item[category] !== undefined && item[category] !== 0)
  );

  const series: SeriesOption[] = filteredCategories.map((category) => ({
    name: category,
    type: 'bar',
    stack: 'Stacked',
    barWidth: '15%',
	// itemStyle: { color: colorMapping[category] || '#ccc' },
    data: chartData.map((item) => item[category] ?? 0) 
  }));

  // Check if 'Overall Growth' line data is valid
  const hasOverallGrowth = !filter?.length && chartData.some((item) => item['totalSizeInBytes'] !== undefined && item['totalSizeInBytes'] !== 0);
  if (hasOverallGrowth) {
    series.push({
      name: 'Overall Growth',
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: {
        color: colorMapping['Overall Growth'] || '#FFA726',
        width: 2
      },
      data: chartData.map((item) => item['totalSizeInBytes'] ?? 0)
    });
  }

  return {
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#003771',
      textStyle: { color: '#A9ADCB' },
      formatter: function (params: any) {
        let tooltipContent = `<strong>${params[0].name}</strong><br>`;
        
        // Iterate through the series to display data for the current month
        params.forEach((param: any) => {
          if (param.value !== 0) {
            tooltipContent += `${param.seriesName}: ${param.value} ${chartData[0]?.unit || ''}<br>`;
          }
        });
        
        return tooltipContent;
      }
    },
    legend: {
      bottom: '20',
      left: '0',
      orient: 'horizontal',
      textStyle: { color: '#A9ADCB' },
      itemGap: 20,
      data: [...filteredCategories, ...(hasOverallGrowth ? ['Overall Growth'] : [])],
      pageIconColor: '#A9ADCB',
      pageIconInactiveColor: '#444C63',
      pageTextStyle: {
        color: '#A9ADCB',
        fontSize: 10
      },
      type: 'scroll',
      scrollDataIndex: 0,
	  pageButtonItemGap: 10,
	  pageButtonPosition: 'end',
      width: window.innerWidth < 600 ? '90%' : 'auto',
    },
    grid: {
      left: '2%',
      right: '3%',
      bottom: '14%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      data: chartData.map((item) => dayjs(item.month).format('MMM YYYY')),
      axisLabel: { color: '#A9ADCB', rotate: 30 },
      axisTick: { show: false }
    },
    yAxis: {
      axisLabel: {
        color: '#A9ADCB',
        formatter: (value: number) => `${value} ${chartData[0]?.unit || ''}`,
      },
      splitLine: { show: false }
    },
    series: series
  };
};

const StackedBarLineTrendChart = ({
	title = '',
	isLoading = false,
	data = [],
	colorMapping = {},
  filter = [],
	optionsFunction = getStackedBarLineChartOptions
}: StackedBarLineTrendChartProps) => {
	const chartRef = useRef<ReactECharts>(null);
	const chartContainerRef = useRef<HTMLDivElement>(null);
	const [windowSize, setWindowSize] = useState<number>(window.innerWidth);
	const [appliedFilter, setAppliedFilter] = useState<any[]>(filter);
  const chartOption = optionsFunction(data, colorMapping);

  useEffect(() => {
    setAppliedFilter(filter)
  }, [filter])
  
	useEffect(() => {
		const handleResize = debounce(() => {
			if (chartRef.current) {
				chartRef.current?.getEchartsInstance().resize();
			}
			setWindowSize(window.innerWidth);
		}, 200);

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

  useEffect(() => {
    console.log(appliedFilter)
    console.log(chartRef.current)
    if (chartRef.current && appliedFilter.length) {
      const chartInstance = chartRef.current?.getEchartsInstance()
      if(chartInstance) {
        setTimeout(()=>{
          let seriesDataOptions:SeriesOption | SeriesOption[] | undefined = chartInstance?.getOption()?.series || chartOption.series
          if(Array.isArray(seriesDataOptions)){
            seriesDataOptions = seriesDataOptions?.map(s => s.name)
            for(const option of seriesDataOptions ){
              if(appliedFilter.includes(option)){
                chartInstance.dispatchAction({
                  type: 'legendSelect',
                  name: option
                })
              } else {
                chartInstance.dispatchAction({
                  type: 'legendUnSelect',
                  name: option
                })
              }
            }
          }
        },1000)
      }
    }
  },[appliedFilter])

	return (
		<Box
			ref={chartContainerRef}
			sx={{
				flex: '1 1 auto',
				height: '356px',
				width: '100%',
				p: 2,
				// overflowX: 'auto',
			}}
		>
			<Typography sx={{ fontSize: { xs: '18px', lg: '20px' } }} fontWeight={500}>
				{title}
			</Typography>
			{!isLoading ? (
				<ReactECharts
					key={windowSize}
					ref={chartRef}
					option={chartOption}
					style={{ width: '100%', height: '100%' }}
					opts={{ renderer: 'canvas', width: 'auto' }}
					notMerge={true}
					lazyUpdate={true}
				/>
			) : (
				<LoaderComponent />
			)}
		</Box>
	);
};

export default StackedBarLineTrendChart;
